export type FeatureStatus = 'full' | 'partial' | 'none'

export interface Tool {
  id: string
  name: string
  tagline: string
  bestFor: string
  strengths: string[]
  watchOuts: string[]
  pricingModel: string
  deployment: string
}

export interface Feature {
  id: string
  name: string
  values: Record<string, FeatureStatus>
}

export interface FeatureCategory {
  name: string
  features: Feature[]
}

export const tools: Tool[] = [
  {
    id: 'waydev',
    name: 'Waydev',
    tagline:
      'The all-in-one engineering intelligence platform — from git analytics to R&D capitalization.',
    bestFor: 'End-to-end engineering intelligence',
    strengths: [
      'Complete DORA + output & impact metrics',
      'Built-in R&D cost capitalization',
      'Executive-ready dashboards out of the box',
    ],
    watchOuts: [
      'Best value when used platform-wide',
      'Rich feature set has a small learning curve',
    ],
    pricingModel: 'Per-contributor',
    deployment: 'SaaS / On-prem',
  },
  {
    id: 'dx',
    name: 'DX',
    tagline: 'Developer experience measurement & research-backed insights.',
    bestFor: 'Improving developer experience',
    strengths: [
      'Research-backed DXI framework',
      'Excellent qualitative survey tools',
      'Executive-friendly reporting',
    ],
    watchOuts: [
      'Premium pricing model',
      'Heavy reliance on survey participation',
    ],
    pricingModel: 'Per-seat (Premium)',
    deployment: 'SaaS',
  },
  {
    id: 'linearb',
    name: 'LinearB',
    tagline: 'Workflow analytics and PR automation for engineering teams.',
    bestFor: 'Speeding up PR cycles',
    strengths: [
      'gitStream workflow automation',
      'Strong DORA metrics tracking',
      'Engineering-to-business translation',
    ],
    watchOuts: ['Highly git-data centric', 'Can feel noisy without tuning'],
    pricingModel: 'Per-contributor',
    deployment: 'SaaS / On-prem',
  },
  {
    id: 'jellyfish',
    name: 'Jellyfish',
    tagline: 'Engineering management platform for business alignment.',
    bestFor: 'R&D capitalization & exec alignment',
    strengths: [
      'Deep R&D allocation tracking',
      'Robust executive dashboards',
      'Strong business context mapping',
    ],
    watchOuts: [
      'Enterprise-focused and pricey',
      'Less popular with IC developers',
    ],
    pricingModel: 'Custom Enterprise',
    deployment: 'SaaS',
  },
  {
    id: 'swarmia',
    name: 'Swarmia',
    tagline: 'Developer-friendly metrics combining systems and surveys.',
    bestFor: 'Team-level continuous improvement',
    strengths: [
      'High transparency for developers',
      'Balances system data with surveys',
      'Investment distribution tracking',
    ],
    watchOuts: [
      'Smaller vendor ecosystem',
      'Fewer legacy enterprise integrations',
    ],
    pricingModel: 'Per-seat',
    deployment: 'SaaS',
  },
  {
    id: 'uplevel',
    name: 'Uplevel',
    tagline:
      'Engineering effectiveness from git, Jira, calendar & comms signals.',
    bestFor: 'Deep work & meeting load insights',
    strengths: [
      'Unique meeting & focus-time analytics',
      'Multi-signal effectiveness scoring',
      'Proactive team-health alerts',
    ],
    watchOuts: [
      'Broad data integration footprint',
      'Can raise developer surveillance concerns',
    ],
    pricingModel: 'Per-seat',
    deployment: 'SaaS',
  },
  {
    id: 'sleuth',
    name: 'Sleuth',
    tagline: 'Lightweight DORA metrics and deployment tracking.',
    bestFor: 'Fast DORA implementation',
    strengths: [
      'Extremely fast time-to-value',
      'Accurate deploy tracking',
      'Lightweight and focused',
    ],
    watchOuts: [
      'Narrower scope than full EMPs',
      'Lacks deep financial tracking',
    ],
    pricingModel: 'Per-developer',
    deployment: 'SaaS',
  },
  {
    id: 'harness',
    name: 'Harness SEI',
    tagline: 'Engineering insights tied directly to the delivery pipeline.',
    bestFor: 'Existing Harness customers',
    strengths: [
      'Deep CI/CD pipeline integration',
      'Enterprise-grade governance',
      'Customizable dashboards',
    ],
    watchOuts: [
      'Best utilized within Harness ecosystem',
      'Steeper learning curve',
    ],
    pricingModel: 'Custom Enterprise',
    deployment: 'SaaS / On-prem',
  },
  {
    id: 'codeclimate',
    name: 'Code Climate',
    tagline: 'Git and PR analytics with a focus on code quality.',
    bestFor: 'Code review optimization',
    strengths: [
      'Deep PR and review analytics',
      'Code quality tie-ins',
      'Established market presence',
    ],
    watchOuts: [
      'Interface feels slightly dated',
      'Lacks modern DevEx survey tools',
    ],
    pricingModel: 'Per-seat',
    deployment: 'SaaS / On-prem',
  },
  {
    id: 'pluralsight',
    name: 'Pluralsight Flow',
    tagline: 'Git analytics and retrospective metrics for engineering.',
    bestFor: 'Legacy git analytics',
    strengths: [
      'Deep historical git analysis',
      'Part of broader Pluralsight suite',
      'Detailed contributor metrics',
    ],
    watchOuts: [
      'Metric-mining concerns from devs',
      'Dated approach to engineering health',
    ],
    pricingModel: 'Per-seat',
    deployment: 'SaaS / On-prem',
  },
  {
    id: 'hatica',
    name: 'Hatica',
    tagline: 'Lightweight DORA and workflow analytics for growing teams.',
    bestFor: 'SMBs needing quick insights',
    strengths: [
      'SMB and mid-market friendly',
      'Clean, modern interface',
      'Good baseline DORA tracking',
    ],
    watchOuts: [
      'Lacks deep enterprise customization',
      'Newer entrant to the market',
    ],
    pricingModel: 'Per-seat',
    deployment: 'SaaS',
  },
]

export const matrixData: FeatureCategory[] = [
  {
    name: 'Metrics & Frameworks',
    features: [
      {
        id: 'dora',
        name: 'DORA Metrics',
        values: {
          waydev: 'full',
          dx: 'partial',
          linearb: 'full',
          jellyfish: 'full',
          swarmia: 'full',
          uplevel: 'full',
          sleuth: 'full',
          harness: 'full',
          codeclimate: 'full',
          pluralsight: 'full',
          hatica: 'full',
        },
      },
      {
        id: 'space',
        name: 'SPACE Framework',
        values: {
          waydev: 'full',
          dx: 'full',
          linearb: 'partial',
          jellyfish: 'partial',
          swarmia: 'full',
          uplevel: 'full',
          sleuth: 'partial',
          harness: 'partial',
          codeclimate: 'none',
          pluralsight: 'none',
          hatica: 'partial',
        },
      },
      {
        id: 'devex',
        name: 'DevEx Surveys',
        values: {
          waydev: 'partial',
          dx: 'full',
          linearb: 'none',
          jellyfish: 'partial',
          swarmia: 'full',
          uplevel: 'partial',
          sleuth: 'none',
          harness: 'partial',
          codeclimate: 'none',
          pluralsight: 'none',
          hatica: 'partial',
        },
      },
      {
        id: 'custom',
        name: 'Custom Metrics',
        values: {
          waydev: 'full',
          dx: 'full',
          linearb: 'partial',
          jellyfish: 'full',
          swarmia: 'partial',
          uplevel: 'partial',
          sleuth: 'partial',
          harness: 'full',
          codeclimate: 'none',
          pluralsight: 'none',
          hatica: 'none',
        },
      },
    ],
  },
  {
    name: 'Workflow & Automation',
    features: [
      {
        id: 'pr_analytics',
        name: 'PR Analytics',
        values: {
          waydev: 'full',
          dx: 'partial',
          linearb: 'full',
          jellyfish: 'full',
          swarmia: 'full',
          uplevel: 'full',
          sleuth: 'full',
          harness: 'full',
          codeclimate: 'full',
          pluralsight: 'full',
          hatica: 'full',
        },
      },
      {
        id: 'automation',
        name: 'Workflow Automation',
        values: {
          waydev: 'partial',
          dx: 'none',
          linearb: 'full',
          jellyfish: 'none',
          swarmia: 'partial',
          uplevel: 'none',
          sleuth: 'partial',
          harness: 'partial',
          codeclimate: 'none',
          pluralsight: 'none',
          hatica: 'none',
        },
      },
      {
        id: 'bottleneck',
        name: 'Bottleneck Detection',
        values: {
          waydev: 'full',
          dx: 'partial',
          linearb: 'full',
          jellyfish: 'full',
          swarmia: 'full',
          uplevel: 'full',
          sleuth: 'full',
          harness: 'full',
          codeclimate: 'full',
          pluralsight: 'full',
          hatica: 'full',
        },
      },
    ],
  },
  {
    name: 'Business Alignment',
    features: [
      {
        id: 'allocation',
        name: 'R&D Allocation',
        values: {
          waydev: 'full',
          dx: 'none',
          linearb: 'full',
          jellyfish: 'full',
          swarmia: 'full',
          uplevel: 'partial',
          sleuth: 'none',
          harness: 'full',
          codeclimate: 'none',
          pluralsight: 'none',
          hatica: 'partial',
        },
      },
      {
        id: 'capitalization',
        name: 'R&D Capitalization',
        values: {
          waydev: 'full',
          dx: 'none',
          linearb: 'partial',
          jellyfish: 'full',
          swarmia: 'none',
          uplevel: 'none',
          sleuth: 'none',
          harness: 'full',
          codeclimate: 'none',
          pluralsight: 'none',
          hatica: 'none',
        },
      },
      {
        id: 'exec_dash',
        name: 'Exec Dashboards',
        values: {
          waydev: 'full',
          dx: 'full',
          linearb: 'full',
          jellyfish: 'full',
          swarmia: 'partial',
          uplevel: 'full',
          sleuth: 'partial',
          harness: 'full',
          codeclimate: 'partial',
          pluralsight: 'partial',
          hatica: 'partial',
        },
      },
    ],
  },
  {
    name: 'AI Intelligence',
    features: [
      {
        id: 'ai_impact',
        name: 'AI Impact',
        values: {
          waydev: 'full',
          dx: 'partial',
          linearb: 'none',
          jellyfish: 'none',
          swarmia: 'none',
          uplevel: 'none',
          sleuth: 'none',
          harness: 'none',
          codeclimate: 'none',
          pluralsight: 'none',
          hatica: 'none',
        },
      },
      {
        id: 'ai_roi',
        name: 'AI ROI',
        values: {
          waydev: 'full',
          dx: 'none',
          linearb: 'none',
          jellyfish: 'partial',
          swarmia: 'none',
          uplevel: 'none',
          sleuth: 'none',
          harness: 'none',
          codeclimate: 'none',
          pluralsight: 'none',
          hatica: 'none',
        },
      },
      {
        id: 'ai_adoption',
        name: 'AI Adoption',
        values: {
          waydev: 'full',
          dx: 'partial',
          linearb: 'none',
          jellyfish: 'none',
          swarmia: 'none',
          uplevel: 'none',
          sleuth: 'none',
          harness: 'none',
          codeclimate: 'none',
          pluralsight: 'none',
          hatica: 'none',
        },
      },
    ],
  },
  {
    name: 'Integrations & Setup',
    features: [
      {
        id: 'git',
        name: 'Git Providers',
        values: {
          waydev: 'full',
          dx: 'full',
          linearb: 'full',
          jellyfish: 'full',
          swarmia: 'full',
          uplevel: 'full',
          sleuth: 'full',
          harness: 'full',
          codeclimate: 'full',
          pluralsight: 'full',
          hatica: 'full',
        },
      },
      {
        id: 'jira',
        name: 'Jira / Linear',
        values: {
          waydev: 'full',
          dx: 'full',
          linearb: 'full',
          jellyfish: 'full',
          swarmia: 'full',
          uplevel: 'full',
          sleuth: 'full',
          harness: 'full',
          codeclimate: 'full',
          pluralsight: 'full',
          hatica: 'full',
        },
      },
      {
        id: 'cicd',
        name: 'CI/CD Tools',
        values: {
          waydev: 'full',
          dx: 'partial',
          linearb: 'full',
          jellyfish: 'full',
          swarmia: 'full',
          uplevel: 'partial',
          sleuth: 'full',
          harness: 'full',
          codeclimate: 'partial',
          pluralsight: 'partial',
          hatica: 'full',
        },
      },
      {
        id: 'ttv',
        name: 'Fast Time-to-Value',
        values: {
          waydev: 'full',
          dx: 'partial',
          linearb: 'partial',
          jellyfish: 'none',
          swarmia: 'full',
          uplevel: 'partial',
          sleuth: 'full',
          harness: 'none',
          codeclimate: 'partial',
          pluralsight: 'partial',
          hatica: 'full',
        },
      },
    ],
  },
]
